import * as React from 'react';
import Paragraph from 'antd/lib/typography/Paragraph';
import Modal from 'antd/lib/modal/Modal';
import Image from 'antd/lib/image';
import Spin from 'antd/lib/spin';
import MarkdownComponent from './HOCs/Markdown';
import { ChecklistAttachment } from '../types';
import { antGrey } from './styles/globalStyles';
import { getChecklistAttachmentURLs } from '../api';
import RetryableChecklistAttachmentImage from './checklistItems/RetryableChecklistAttachmentImage';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';

export interface ChecklistItemInfo {
    title: string;
    description: string;
    attachments?: ChecklistAttachment[];
}

interface ChecklistItemAdditionalInfoModalProps {
    isVisible: boolean;
    title: string;
    description: string;
    attachments?: ChecklistAttachment[];
    orgId?: string;
    workflowId?: string;
    onCancel: () => void;
    afterClose: () => void;
}

const ChecklistItemAdditionalInfoModal = (props: ChecklistItemAdditionalInfoModalProps) => {
    const { isVisible, title, description, attachments, orgId, workflowId, onCancel, afterClose } = props;
    const auth = useSelector((state: RootState) => state.auth.authCredentials);
    const [imageUrls, setImageUrls] = React.useState<Record<string, string>>({});
    const [loading, setLoading] = React.useState(false);

    const attachmentKeys =
        attachments
            ?.map((att) => att.s3Key)
            .sort()
            .join(',') ?? '';

    React.useEffect(() => {
        if (!isVisible || !attachments?.length || !auth || !orgId || !workflowId) {
            return;
        }
        // Skip if we already have URLs for these attachments
        const allLoaded = attachments.every((att) => imageUrls[att.s3Key]);
        if (allLoaded) return;

        const s3Keys = attachments.map((att) => att.s3Key).filter(Boolean);
        if (s3Keys.length === 0) return;

        setLoading(true);
        getChecklistAttachmentURLs(orgId, workflowId, s3Keys, auth)
            .then((response) => setImageUrls((prev) => ({ ...prev, ...(response.data ?? {}) })))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [isVisible, attachmentKeys]);

    const visibleAttachments = attachments?.filter((att) => imageUrls[att.s3Key]) ?? [];

    return (
        <Modal
            open={isVisible}
            onCancel={onCancel}
            afterClose={afterClose}
            width="70%"
            style={{ minWidth: 500, maxWidth: 800 }}
            footer={null}
            title={
                <Paragraph ellipsis={{ rows: 2 }} strong>
                    {title}
                </Paragraph>
            }
        >
            {description && <MarkdownComponent source={description} />}
            {attachments && attachments?.length > 0 && (
                <div style={{ marginTop: description ? 16 : 0 }}>
                    {loading ?
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {attachments.map((att) => (
                                <div
                                    key={att.s3Key}
                                    style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: 4,
                                        border: `1px solid ${antGrey.grey4}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: antGrey.grey2,
                                    }}
                                >
                                    <Spin size="small" />
                                </div>
                            ))}
                        </div>
                    :   visibleAttachments.length > 0 && (
                            <Image.PreviewGroup>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 8,
                                    }}
                                >
                                    {visibleAttachments.map((att) => (
                                        <RetryableChecklistAttachmentImage
                                            key={att.s3Key}
                                            src={imageUrls[att.s3Key]}
                                            alt={att.fileName}
                                            width={120}
                                            height={120}
                                        />
                                    ))}
                                </div>
                            </Image.PreviewGroup>
                        )
                    }
                </div>
            )}
        </Modal>
    );
};

export default ChecklistItemAdditionalInfoModal;
